import styled, { keyframes, css } from "styled-components";

/* ========================================================================
   Neo skin — Stripe-inspired compact two-column hosted page.
   Both panels are white. A single thin vertical divider separates them.
   ======================================================================== */

/* ─── Shimmer skeleton ─────────────────────────────────────────────────── */

const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position:  400px 0; }
`;

const shimmerBase = css`
  background: #f0f2f5;
  background-image: linear-gradient(
    90deg,
    #f0f2f5 0px,
    #e8eaed 40px,
    #f0f2f5 80px
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;
  border-radius: 4px;
`;

export const SkeletonBlock = styled.div`
  ${shimmerBase}
  height: ${(p) => p.$h || "14px"};
  width: ${(p) => p.$w || "100%"};
  border-radius: ${(p) => p.$r || "4px"};
  margin-bottom: ${(p) => p.$mb || "0"};
  flex-shrink: 0;
`;

export const SkeletonCircle = styled.div`
  ${shimmerBase}
  width: ${(p) => p.$size || "32px"};
  height: ${(p) => p.$size || "32px"};
  border-radius: 50%;
  flex-shrink: 0;
`;

export const SkeletonRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: ${(p) => p.$mb || "12px"};
`;

export const SkeletonLineItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
`;

export const SkeletonLineBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const NeoPageWrapper = styled.div`
  min-height: 100vh;
  background-color: #ffffff;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 16px 48px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue",
    Roboto, sans-serif;
  color: #1a1f36;
`;

export const NeoCard = styled.div`
  display: flex;
  width: 100%;
  max-width: 1024px;
  background: #ffffff;
  border-radius: 4px;
  overflow: visible;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

/* ============================ LEFT PANEL ============================ */

export const NeoLeftPanel = styled.div`
  width: 44%;
  padding: 16px 32px 24px 8px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  border-right: 1px solid #e6e6e6;

  @media (max-width: 768px) {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #e6e6e6;
    padding: 16px 16px 24px;
  }
`;

export const NeoHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 18px;
`;

export const NeoBackArrow = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6a7383;
  padding: 0 4px 0 0;
  display: flex;
  align-items: center;
  font-size: 18px;
  line-height: 1;

  &:hover {
    color: #1a1f36;
  }
`;

export const NeoLogoCircle = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: ${(p) => (p.$hasImage ? "transparent" : p.$color || "#3a75c4")};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

export const NeoMerchantName = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #1a1f36;
`;

export const NeoSubscribeLabel = styled.p`
  font-size: 14px;
  color: #6a7383;
  margin: 0 0 4px;
  line-height: 1.4;
`;

export const NeoBigPrice = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #1a1f36;
  letter-spacing: -0.5px;
  line-height: 1.2;
  margin-bottom: 6px;
`;

export const NeoTrialCopy = styled.p`
  font-size: 13px;
  color: #6a7383;
  margin: 0 0 24px;
  line-height: 1.5;

  strong {
    color: #1a1f36;
    font-weight: 600;
  }
`;

export const NeoPlanGroup = styled.div`
  margin-bottom: 16px;
`;

export const NeoLineItemRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 0;
`;

export const NeoAddonList = styled.div`
  margin: 10px 0 0 44px;
  padding-left: 14px;
  border-left: 2px solid #e6e8eb;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const NeoAddonRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

export const NeoAddonBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const NeoAddonLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #8898aa;
`;

export const NeoLineThumb = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 4px;
  background-color: ${(p) => p.$color || "#3a75c4"};
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

export const NeoLineBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const NeoLineTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #1a1f36;
`;

export const NeoLineSub = styled.div`
  font-size: 12px;
  color: #6a7383;
  line-height: 1.3;
`;

export const NeoLinePrice = styled.div`
  font-size: 14px;
  color: #1a1f36;
  font-weight: 500;
  white-space: nowrap;
`;

export const NeoSummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  font-size: 14px;
  color: ${(p) => (p.$bold ? "#1a1f36" : "#1a1f36")};
  font-weight: ${(p) => (p.$bold ? "600" : "400")};
`;

export const NeoSummaryLabelMuted = styled.span`
  color: #6a7383;
  font-size: 13px;
`;

export const NeoCouponPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px 4px 8px;
  border-radius: 4px;
  background: #f6f8fa;
  border: 1px solid #e6e6e6;
  font-size: 12px;
  color: #1a1f36;
  font-weight: 500;

  svg {
    width: 12px;
    height: 12px;
    color: #6a7383;
  }
`;

export const NeoCouponNote = styled.div`
  font-size: 12px;
  color: #6a7383;
  margin: 4px 0 8px;
`;

export const NeoCouponInputRow = styled.div`
  display: flex;
  gap: 6px;
  align-items: stretch;
`;

export const NeoCouponInput = styled.input`
  flex: 1;
  padding: 8px 10px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 13px;
  color: #1a1f36;
  background: #ffffff;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus {
    border-color: #635bff;
    box-shadow: 0 0 0 3px rgba(99, 91, 255, 0.15);
  }

  &::placeholder {
    color: #a3acb9;
  }
`;

export const NeoCouponRedeemBtn = styled.button`
  border: none;
  border-radius: 4px;
  padding: 0 14px;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  background-color: #1a1f36;
  cursor: ${(p) => (p.disabled ? "not-allowed" : "pointer")};
  opacity: ${(p) => (p.disabled ? 0.5 : 1)};
  white-space: nowrap;

  &:hover:not(:disabled) {
    background-color: #2c3144;
  }
`;

export const NeoCouponError = styled.div`
  color: #df1b41;
  font-size: 12px;
  margin-top: 6px;
`;

export const NeoCouponChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  margin-top: 8px;
`;

export const NeoCouponChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 4px 3px 10px;
  border-radius: 999px;
  border: 1px solid #e6e6e6;
  background: #f6f8fa;
  font-size: 12px;
  color: #1a1f36;
`;

export const NeoCouponRemoveBtn = styled.button`
  border: none;
  background: transparent;
  color: #6a7383;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  padding: 0 4px;
  border-radius: 999px;

  &:hover:not(:disabled) {
    color: #1a1f36;
  }
`;

export const NeoCouponClearAll = styled.button`
  border: none;
  background: transparent;
  color: #635bff;
  cursor: pointer;
  font-size: 12px;
  text-decoration: underline;
  padding: 0;
`;

export const NeoDivider = styled.div`
  height: 1px;
  background-color: #e6e6e6;
  margin: 8px 0;
`;

export const NeoTaxLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;

  svg {
    width: 14px;
    height: 14px;
    color: #a3acb9;
  }
`;

/* ============================ RIGHT PANEL ============================ */

export const NeoRightPanel = styled.div`
  flex: 1;
  padding: 16px 8px 24px 32px;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: 24px 16px 32px;
  }
`;

export const NeoTopRightControls = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
`;

export const NeoSectionLabel = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #1a1f36;
  margin: 0 0 8px;
`;

export const NeoFieldLabel = styled.label`
  display: block;
  font-size: 13px;
  color: #1a1f36;
  margin: 12px 0 4px;
`;

export const NeoInput = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  color: #1a1f36;
  background: #ffffff;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:focus {
    border-color: #635bff;
    box-shadow: 0 0 0 3px rgba(99, 91, 255, 0.15);
  }

  &::placeholder {
    color: #a3acb9;
  }
`;

export const NeoContactSection = styled.div`
  margin-bottom: 20px;
`;

/* Compact override for CustomerInfoForm — keeps validation/required logic
   intact but visually fits Neo's tight column. Tightens labels, inputs,
   and section titles. */
export const NeoCustomerFormSlot = styled.div`
  /* Section title (e.g. "Billing Address") */
  & h2 {
    font-size: 14px;
    font-weight: 600;
    margin: 16px 0 8px;
    color: #1a1f36;
  }

  /* Field labels */
  & label {
    font-size: 13px;
    margin-bottom: 4px;
    color: #1a1f36;
  }

  /* Inputs and selects */
  & input,
  & select {
    padding: 9px 12px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font-size: 14px;
    color: #1a1f36;
    background: #ffffff;

    &:focus {
      outline: none;
      border-color: #635bff;
      box-shadow: 0 0 0 3px rgba(99, 91, 255, 0.15);
    }
  }

  /* Reduce row gaps for compact layout */
  & > div > div[class*="FormRow"],
  & > div > div {
    margin-bottom: 10px;
  }

  /* Reduce overall section bottom margin */
  & > div:last-child {
    margin-bottom: 0;
  }
`;

export const NeoPaymentSection = styled.div`
  margin-bottom: 16px;
`;

/* Compact override for the existing PaymentMethodSelector — turns its options
   into Stripe-like radio rows but preserves all gateway form mounts. */
export const NeoPaymentSlot = styled.div`
  /* Each option becomes a clean bordered radio row */
  & .payment-method-option,
  & [data-payment-option],
  & label.payment-method-option {
    border: 1px solid #e6e6e6;
    border-radius: 4px;
    margin-bottom: 6px;
    padding: 10px 12px;
    background: #ffffff;
    transition: border-color 0.15s, background 0.15s;
    cursor: pointer;

    &:hover {
      border-color: #d1d5db;
      background: #fafbfc;
    }

    &.selected,
    &[data-selected="true"] {
      border-color: #635bff;
      background: #f7f7ff;
    }
  }
`;

export const NeoTermsBlock = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 16px 0 12px;
  font-size: 12px;
  color: #6a7383;
  line-height: 1.5;

  input[type="checkbox"] {
    margin-top: 2px;
    cursor: pointer;
  }

  a {
    color: #6a7383;
    text-decoration: underline;
  }
`;

export const NeoCTASection = styled.div`
  margin-top: 8px;
`;

export const NeoPayButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: none;
  border-radius: 4px;
  font-size: 15px;
  font-weight: 600;
  color: #ffffff;
  cursor: ${(p) => (p.disabled ? "not-allowed" : "pointer")};
  background-color: ${(p) => {
    if (p.disabled) return "#e3e8ee";
    return p.$color || "#e84566";
  }};
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${(p) => {
      if (p.disabled) return "#e3e8ee";
      return p.$hoverColor || "#d23e5c";
    }};
  }

  &:active {
    transform: ${(p) => (p.disabled ? "none" : "translateY(1px)")};
  }
`;

export const NeoApplePaySlot = styled.div`
  width: 100%;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${(p) => (p.$disabled ? 0.5 : 1)};
  pointer-events: ${(p) => (p.$disabled ? "none" : "auto")};

  & > * {
    width: 100%;
  }
  & apple-pay-button {
    width: 100%;
    --apple-pay-button-width: 100%;
    --apple-pay-button-height: 44px;
    --apple-pay-button-border-radius: 4px;
  }
`;

export const NeoFinePrint = styled.p`
  font-size: 12px;
  color: #6a7383;
  line-height: 1.5;
  margin: 12px 0 0;
  text-align: center;
`;

export const NeoFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 11px;
  color: #a3acb9;
  margin-top: 16px;

  a {
    color: #a3acb9;
    text-decoration: none;

    &:hover {
      color: #6a7383;
      text-decoration: underline;
    }
  }

  span {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
`;
